import { effect, inject, Injectable, signal } from '@angular/core';
import { OrderSelectedCustomization } from '../../../../../../models/order/api-models/order-pd-response.model';
import { OrderStateServiceV2 } from '../../order-state.service';
import { OrderFlowType, OrderV2 } from '../../../../../../models/order/order.model';
import { WirelessProductResponseService } from '../../order-api-response/wireless-pd-response.service';
import { OrderCustomizationSubPayload } from '../../../../../../models/order/workflow/order-customization.model';



@Injectable({ providedIn: 'root' })
export class OrderWirelessCustomizationsStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private wirelessPdResponseService = inject(WirelessProductResponseService);

    selectedCustomizations = signal<OrderSelectedCustomization[]>([]);

    constructor() {
        effect(() => {
            const order = this.orderStateService.order();
            const wirelessPdResponse = this.wirelessPdResponseService.productResponse();
            if (order && wirelessPdResponse) {
                this.setupSelectedCustomizations(order);
            }
        });
    }

    hasAttShippedInventory(): boolean {
        const order = this.orderStateService.order();
        const wirelessLines = order?.wirelessFlow?.lineConfigurations;
        let hasAttShippedInventory = false;
        if (wirelessLines) {
            Object.keys(wirelessLines).forEach(lineKey => {
                const line = wirelessLines[lineKey];
                if (line.device?.deviceType?.toLocaleLowerCase() === 'new') {
                    hasAttShippedInventory = true;
                    return;
                }
            });
        }
        return hasAttShippedInventory;
    }

    private setupSelectedCustomizations(order: OrderV2): void {
        if (order) {
            const selectedCustomizations = [];

            if (order.wirelessFlow?.wirelessOffers?.offers?.length) {
                const selectedOffers = order.wirelessFlow?.wirelessOffers?.offers || [];
                for (const offer of selectedOffers) {
                    const customization = this.wirelessPdResponseService.findCustomizationByChoiceCode(offer.choiceCode);
                    if (customization) {
                        const choice = customization.customization.Choices?.Choice.find(c => c.ChoiceCode === offer.choiceCode);
                        selectedCustomizations.push({
                            type: OrderFlowType.Wireless,
                            choice: choice,
                            customizationReference: customization,
                            customization: customization.customization
                        });
                    }
                }
            }

            if (order.wirelessFlow?.serviceLevelSelection && this.hasAttShippedInventory()) {
                const serviceLevelSelection = order.wirelessFlow.serviceLevelSelection;
                if (serviceLevelSelection.customization) {
                    const choice = serviceLevelSelection.customization.Choices?.Choice.find(c => c.ChoiceCode === serviceLevelSelection.choiceCode);
                    selectedCustomizations.push({
                        type: OrderFlowType.Wireless,
                        choice: choice,
                        customizationReference: serviceLevelSelection.customization,
                        customization: serviceLevelSelection.customization
                    });
                }
            }

            selectedCustomizations.sort((a, b) => (a.choice?.CartSeq || 0) - (b.choice?.CartSeq || 0));
            this.selectedCustomizations.set(selectedCustomizations);
        }
    }

    buildCustomizationPayload(): OrderCustomizationSubPayload[] | null {
        const wirelessComponent = this.wirelessPdResponseService.getProductComponent();
        if (!wirelessComponent) return null;

        const wirelessCustomization = this.selectedCustomizations().filter(cust => cust.type === OrderFlowType.Wireless);

        if (wirelessComponent && wirelessCustomization.length > 0) {
            const customizations = [];

            wirelessComponent.Customizations?.Customization.forEach(cust => {
                if (cust.Choices?.Choice?.length) {
                    cust.Choices?.Choice.forEach(choice => {
                        const isSelected = wirelessCustomization.find(selCust => selCust.choice?.ChoiceCode === choice.ChoiceCode);
                        if (isSelected) {
                            customizations.push({
                                Choice: choice.ChoiceCode || '',
                                Code: isSelected.customization?.CustomizationCode || '',
                            });
                        }
                    });
                }
            });
            return customizations.length > 0 ? customizations : null;
        }

        return null;
    }
}
